import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import { HorizontalSliderScrollbar } from "components/elements/horizontalSliderScrollbar"
import TextLockupV2 from "components/elements/textLockupV2"
import { motion, useMotionValue } from "framer-motion"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import "./section-path-v2.scss"

const SectionPathV2 = ({
  callToActionButton,
  colorTheme,
  heading,
  pathCards,
  pathText,
  titleCardHeading,
  titleCardLinks,
  titleCardLinksTitle,
  titleCardText,
  titleCardTitle,
}) => {
  const colorThemeClass = colorTheme === "Blue" ? "DarkBlue" : colorTheme
  const containerRef = useRef(null)
  const sliderRef = useRef(null)
  const scrollBarRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [sliderWidth, setSliderWidth] = useState(0)
  const x = useMotionValue(0)

  useEffect(() => {
    const container = containerRef.current
    const slider = sliderRef.current
    if (!container || !slider) return
    const measure = () => {
      const cw = container.clientWidth
      const sw = slider.scrollWidth
      setContainerWidth(cw)
      setSliderWidth(sw)
      const minX = cw - sw
      x.set(Math.min(0, Math.max(minX, x.get())))
    }
    const ro = new ResizeObserver(measure)
    ro.observe(container)
    ro.observe(slider)
    measure()
    return () => ro.disconnect()
  }, [x])

  const clampX = next => {
    const min = containerWidth - sliderWidth
    return Math.min(0, Math.max(min, next))
  }

  const onWheel = e => {
    let delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
    if (delta === 0) return
    const mode = e.deltaMode ?? 0 // 0=pixel,1=line,2=page
    if (mode === 1) delta *= 16
    else if (mode === 2) delta *= containerWidth
    e.preventDefault()
    x.set(clampX(x.get() - delta))
  }
  return (
    <section className="section-path-v2">
      <div className="container-v2">
        <div className="section-path-v2--header">
          {heading && (
            <h2 className="section-path-v2--heading">
              <TextLockupV2>{heading}</TextLockupV2>
            </h2>
          )}
          {pathText && <h3 className="section-path-v2--text">{pathText}</h3>}
        </div>
        <div>
          <div
            className="section-path-v2--patient-journey-container"
            onWheel={onWheel}
            ref={containerRef}
          >
            <motion.div
              className="patient-journey__container"
              drag="x"
              dragConstraints={{
                left: containerWidth - sliderWidth,
                right: 0,
              }}
              dragElastic={false}
              dragMomentum={false}
              ref={sliderRef}
              style={{ willChange: "transform", x }}
            >
              <ul className={`timeline timeline__${colorThemeClass}`}>
                <li className="timeline-entry">
                  {titleCardTitle && (
                    <h4 className="g-heading-tag card-title">
                      {titleCardTitle}
                    </h4>
                  )}
                  <div
                    className={`patient-journey-card patient-journey-card__${colorThemeClass} services-card`}
                  >
                    <div className="services-card__description">
                      {titleCardHeading && (
                        <p className="g-paragraph title-card-heading">
                          {titleCardHeading}
                        </p>
                      )}
                      {titleCardText && (
                        <p className="g-paragraph title-card-text">
                          {titleCardText}
                        </p>
                      )}
                    </div>
                    <div className="services-received-container">
                      {titleCardLinksTitle && (
                        <p className="g-paragraph">{titleCardLinksTitle}</p>
                      )}
                      <ul className="services-card__treatments">
                        {titleCardLinks &&
                          titleCardLinks.length > 0 &&
                          titleCardLinks.map(link => (
                            <li key={link.id}>
                              <p className="g-paragraph">
                                {link?.linkUrl?.linkUrl && (
                                  <a href={link?.linkUrl?.linkUrl}>
                                    {link?.linkText}
                                  </a>
                                )}
                              </p>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </li>
                {pathCards &&
                  pathCards.length > 0 &&
                  pathCards.map(card => (
                    <li className="timeline-entry" key={card.id}>
                      {card?.title && (
                        <h4 className="g-heading-tag card-title">
                          {card.title}
                        </h4>
                      )}
                      {card?.icon?.file?.url && card?.text?.text && (
                        <div
                          className={`patient-journey-card patient-journey-card__${colorThemeClass} story-card`}
                        >
                          <div className="g-icon story-card__icon">
                            {card?.icon?.file?.url && (
                              <img
                                alt={card?.icon?.title}
                                src={card?.icon?.file?.url}
                              />
                            )}
                          </div>
                          {card?.text?.text && (
                            <h6 className="g-heading-tag">
                              {card?.text?.text}
                            </h6>
                          )}
                        </div>
                      )}
                      {card?.ctaButton &&
                        card?.ctaButton?.path &&
                        card?.ctaButton?.text && (
                          <p className="patient-journey-card__cta-button">
                            <ButtonV2
                              accessibilityLabel={
                                card?.ctaButton?.accessibilityLabel
                              }
                              className={getButtonClasses(
                                card?.ctaButton?.buttonStyle,
                                card?.ctaButton?.buttonSize
                              )}
                              href={card?.ctaButton?.path}
                              key={card?.ctaButton?.id}
                              showArrow={
                                card?.ctaButton?.showArrow === "Yes"
                                  ? true
                                  : false
                              }
                              text={card?.ctaButton?.text}
                            />
                          </p>
                        )}
                    </li>
                  ))}
              </ul>
            </motion.div>
          </div>
          {sliderWidth > containerWidth + 2 && (
            <HorizontalSliderScrollbar
              containerWidth={containerWidth}
              scrollBarRef={scrollBarRef}
              sliderWidth={sliderWidth}
              thumbWidth={48}
              x={x}
            />
          )}
        </div>
      </div>
      {callToActionButton &&
        callToActionButton?.path &&
        callToActionButton?.text && (
          <div className="container-v2">
            <p className="section-path-v2--button under-text-button">
              <ButtonV2
                accessibilityLabel={callToActionButton.accessibilityLabel}
                className={getButtonClasses(
                  callToActionButton.buttonStyle,
                  callToActionButton.buttonSize
                )}
                href={callToActionButton.path}
                key={callToActionButton.id}
                showArrow={
                  callToActionButton.showArrow === "Yes" ? true : false
                }
                text={callToActionButton.text}
              />
            </p>
          </div>
        )}
    </section>
  )
}

export default SectionPathV2
SectionPathV2.propTypes = {
  callToActionButton: PropTypes.shape(ButtonV2.props),
  colorTheme: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  pathCards: PropTypes.array.isRequired,
  pathText: PropTypes.string.isRequired,
  titleCardHeading: PropTypes.string.isRequired,
  titleCardLinks: PropTypes.array.isRequired,
  titleCardLinksTitle: PropTypes.string.isRequired,
  titleCardText: PropTypes.string.isRequired,
  titleCardTitle: PropTypes.string.isRequired,
}
